import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import { useBreakpointValue } from "@shared/hooks/useBreakpointValue";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import { useRef } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import {
  ProductAddonsModalContent,
  VariantQuantityMap,
} from "./ProductAddonsModalContent";
import { addProductToBasket } from "./ProductStep";

interface ProductAddonsModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ProductTypeSerializer;
  modalHeight?: string;
}

const ProductAddonsModal = ({
  isOpen,
  onClose,
  product,
  modalHeight,
}: ProductAddonsModalProps) => {
  const { t } = useTranslation("purchases");
  const items = useRef<VariantQuantityMap>({});
  const publicBasket = usePublicBasket();
  const modalSize = useBreakpointValue({ base: "full", sm: "3xl" }) as
    | "full"
    | "3xl";
  const modalContentMaxH = useBreakpointValue({
    base: "100%",
    sm: "calc(100vh - 8rem)", // 8rem is the default margin of the modal
  });
  const footerCancelButtonDisplay = useBreakpointValue({
    base: "none",
    sm: "block",
  }) as "none" | "block";

  const handleDone = async () => {
    await addProductToBasket(items.current, publicBasket);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      isCentered
      onClose={onClose}
      size={modalSize}
      variant="interactive"
      modalContentProps={{
        minH: modalHeight || modalContentMaxH,
        maxH: modalHeight || modalContentMaxH,
      }}
    >
      <Modal.Header>{t("Select your product!")}</Modal.Header>

      <Modal.Body padding="none" overflowY="auto">
        <ProductAddonsModalContent
          onChange={(c) => {
            items.current = c;
          }}
          product={product}
        />
      </Modal.Body>

      <Modal.Footer>
        <Flex direction="row-reverse" justifyContent="space-between" w="full">
          <Button
            data-testid="add-add-on-product"
            variant="solid"
            onClick={handleDone}
          >
            {t("Add")}
          </Button>
          <Button.Ghost
            data-testid="cancel-add-add-on-product"
            display={footerCancelButtonDisplay}
            onClick={onClose}
          >
            {t("Cancel", { ns: "common" })}
          </Button.Ghost>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductAddonsModal;
